import * as React from 'react';
import { Form } from 'semantic-ui-react';

import { Contexto } from 'src/contexto';
import Panel from 'src/modulos/Matriculas/IngresoNuevaMatricula/componentes/Panel';
import capitalizarPrimera from 'src/modulos/Matriculas/IngresoNuevaMatricula/utils/capitalizarPrimera';
import colores from '../../colores';
import { gastoNotarialPorFormaDePago } from '../../servicios/gastoNotarialPorFormaDePago';
import { Datos as AlumnosDatos } from '../S3Alumnos';
import AbonoInicial from './componentes/AbonoInicial';
import Arancel from './componentes/Arancel';
import FormasPago, {
    Datos as FormasPagoDatos,
    inicial as formasPagoInicial,
} from './componentes/FormasPago';
import GastosNotariales, {
    Datos as GastosNotarialesDatos,
    inicial as gastosNotarialesInicial,
} from './componentes/GastosNotariales';

type SoloEstasPropiedadesAlumno<K extends keyof AlumnosDatos['alumnoPrincipal']> = Extract<
    keyof AlumnosDatos['alumnoPrincipal'],
    K
>;

export interface Datos {
    formasPago: FormasPagoDatos;
    gastoNotarial: GastosNotarialesDatos;
    observaciones: string;
}

type Formas = FormasPagoDatos['forma'];

export const inicial: Datos = {
    formasPago: { ...formasPagoInicial },
    gastoNotarial: { ...gastosNotarialesInicial },
    observaciones: '',
};

export interface Props {
    financieros: Datos;
    alumnos: AlumnosDatos;
    numeroPagare?: number;
    onChange(financieros: Datos): void;
    alCambiarMontosAlumnos(alumnos: AlumnosDatos): void;
    alCambiarNumeroPagare(numeroPagare?: number): void;
}

class Financieros extends React.PureComponent<Props> {
    public static readonly contextType = Contexto;
    public readonly context!: React.ContextType<typeof Contexto>;

    public render() {
        const { financieros, alumnos, numeroPagare, alCambiarNumeroPagare } = this.props;

        const presentaAbonoInicial =
            financieros.formasPago.forma === 'pagare' || financieros.formasPago.forma === 'cheque'
                ? financieros.formasPago[financieros.formasPago.forma].presentaAbonoInicial
                : false;

        const todosLosAlumnos = [alumnos.alumnoPrincipal, ...alumnos.otrosAlumnos];

        return (
            <Panel titulo='Datos financieros' color={colores.panel}>
                <Arancel
                    todosLosAlumnos={todosLosAlumnos}
                    alCambiarMontos={(monto, indiceAlumno) => {
                        this.alCambiarMontoAlumno('montoArancel', monto, indiceAlumno);
                    }}
                />

                <FormasPago
                    formasPago={financieros.formasPago}
                    onChange={formasPago => {
                        this.procesarCambios({ ...financieros, formasPago });
                    }}
                    alCambiarFormaDePago={this.alCambiarFormaDePago}
                />

                {this.mostrarNumeroPagare() && (
                    <Form.Input
                        label='Nº de pagaré'
                        type='number'
                        value={numeroPagare || ''}
                        onChange={(_e, { value }) => {
                            const numero =
                                value && value.length > 0 ? parseInt(value, 10) : undefined;
                            alCambiarNumeroPagare(numero);
                        }}
                        width='3'
                    />
                )}

                {presentaAbonoInicial && (
                    <AbonoInicial
                        todosLosAlumnos={todosLosAlumnos}
                        alCambiarMontos={(monto, indiceAlumno) => {
                            this.alCambiarMontoAlumno('abonoInicial', monto, indiceAlumno);
                        }}
                    />
                )}

                <GastosNotariales
                    gastoNotarial={financieros.gastoNotarial}
                    todosLosAlumnos={todosLosAlumnos}
                    onChange={gastoNotarial => {
                        this.procesarCambios({ ...financieros, gastoNotarial });
                    }}
                    alCambiarMontos={(monto, indiceAlumno) => {
                        this.alCambiarMontoAlumno('montoGastoNotarial', monto, indiceAlumno);
                    }}
                />

                <Panel titulo='Observaciones financieras'>
                    <Form.TextArea
                        placeholder='Escriba aquí...'
                        value={financieros.observaciones}
                        onChange={(_e, { value }) => {
                            this.procesarCambios({
                                ...financieros,
                                observaciones: capitalizarPrimera((value as string) || ''),
                            });
                        }}
                    />
                </Panel>
            </Panel>
        );
    }

    private readonly alCambiarFormaDePago = (forma: Formas) => {
        const { financieros } = this.props;

        this.props.alCambiarMontosAlumnos({
            ...this.props.alumnos,
            alumnoPrincipal: {
                ...this.props.alumnos.alumnoPrincipal,
                montoGastoNotarial: gastoNotarialPorFormaDePago(forma),
            },
            otrosAlumnos: this.props.alumnos.otrosAlumnos.map(cadaAlumno => ({
                ...cadaAlumno,
                montoGastoNotarial: gastoNotarialPorFormaDePago(forma),
            })),
        });

        this.procesarCambios({
            ...financieros,
            formasPago: {
                ...financieros.formasPago,
                forma,
            },
            gastoNotarial: {
                ...financieros.gastoNotarial,
            },
        });
    };

    private alCambiarMontoAlumno(
        tipo: SoloEstasPropiedadesAlumno<'montoArancel' | 'montoGastoNotarial' | 'abonoInicial'>,
        monto: number,
        indiceAlumno: number,
    ) {
        const { alCambiarMontosAlumnos } = this.props;

        if (indiceAlumno === 0) {
            alCambiarMontosAlumnos({
                ...this.props.alumnos,
                alumnoPrincipal: {
                    ...this.props.alumnos.alumnoPrincipal,
                    [tipo]: monto,
                },
            });
        } else {
            const otrosAlumnos = [...this.props.alumnos.otrosAlumnos];
            const alumno = otrosAlumnos[indiceAlumno - 1];
            otrosAlumnos[indiceAlumno - 1] = {
                ...alumno,
                [tipo]: monto,
            };

            alCambiarMontosAlumnos({
                ...this.props.alumnos,
                otrosAlumnos,
            });
        }

        this.procesarCambios(this.props.financieros);
    }

    private procesarCambios(financieros: Datos) {
        const { onChange } = this.props;

        onChange({
            ...financieros,
            gastoNotarial: {
                ...financieros.gastoNotarial,
            },
        });
    }

    private mostrarNumeroPagare() {
        return false;
    }
}

export default Financieros;



// WEBPACK FOOTER //
// ./src/modulos/Matriculas/IngresoNuevaMatricula/componentes/S4Financieros/index.tsx